import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileDown, Loader2, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/helpconnect-faqs`;
const EMPTY = { question: '', answer: '', order: 0, published: true };

const FaqDialog = ({ faq, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(faq ? { ...EMPTY, ...faq } : EMPTY); }, [open, faq]);
  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) return toast.error('Add a question and an answer');
    setSaving(true);
    try {
      const body = { ...form, order: Number(form.order) || 0 };
      if (faq) await axios.put(`${API}/${faq.id}`, body); else await axios.post(API, body);
      toast.success('FAQ saved');
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the FAQ'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="faq-form-dialog">
        <DialogHeader><DialogTitle>{faq ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Question</Label><Input value={form.question} maxLength={300} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question-input" /></div>
          <div><Label>Answer</Label><Textarea rows={6} value={form.answer} maxLength={4000} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-answer-input" /></div>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm">Order<Input type="number" className="w-20" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="faq-order-input" /></label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="faq-published-switch" />Published</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="faq-save-btn">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdminFaqsTab = () => {
  const [faqs, setFaqs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { axios.get(API).then((r) => setFaqs(r.data.faqs)).catch(() => toast.error('Failed to load FAQs')); }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.question}"?`)) return;
    try { await axios.delete(`${API}/${f.id}`); toast.success('FAQ deleted'); load(); } catch { toast.error('Could not delete'); }
  };
  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${data.imported} FAQ${data.imported === 1 ? '' : 's'}${data.skipped ? `, skipped ${data.skipped} (blank, too long or duplicate)` : ''}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not import the CSV'); }
  };
  const template = () => {
    const url = URL.createObjectURL(new Blob(['question,answer\n"How do I sign in?","Use the email and password you created from your setup link."\n'], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'helpconnect-faq-template.csv'; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <Card data-testid="admin-faqs-tab">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <div><CardTitle className="text-base">Support FAQs</CardTitle><CardDescription>Published FAQs appear on the HELPConnect Support page, sorted by order. Import a CSV with question and answer columns to add more.</CardDescription></div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={template} data-testid="faq-template-btn"><FileDown className="h-4 w-4 mr-2" />CSV Template</Button>
          <Button variant="outline" asChild data-testid="faq-import-btn">
            <label className="cursor-pointer"><Upload className="h-4 w-4 mr-2" />Import CSV
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} data-testid="faq-import-input" /></label>
          </Button>
          <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-faq-btn"><Plus className="h-4 w-4 mr-2" />Add FAQ</Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {!faqs ? <Loader2 className="h-5 w-5 animate-spin m-6" /> : faqs.length === 0 ? <p className="p-6 text-sm text-muted-foreground" data-testid="admin-faqs-empty">No FAQs yet.</p> : (
          <div className="divide-y border-t">
            {faqs.map((f) => (
              <div key={f.id} className="flex flex-col md:flex-row md:items-center gap-3 p-4" data-testid={`admin-faq-row-${f.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2"><span className="font-semibold">{f.question}</span>
                    <Badge variant={f.published ? 'default' : 'secondary'}>{f.published ? 'Published' : 'Draft'}</Badge>
                    <span className="text-xs text-muted-foreground">Order {f.order}</span>
                    {f.solved_count > 0 && <span className="text-xs text-muted-foreground" data-testid={`faq-solved-count-${f.id}`}>· Solved {f.solved_count} {f.solved_count === 1 ? 'request' : 'requests'}</span>}</div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{f.answer}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => { setEditing(f); setOpen(true); }} data-testid={`admin-faq-edit-${f.id}`}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(f)} aria-label="Delete" data-testid={`admin-faq-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <FaqDialog faq={editing} open={open} onOpenChange={setOpen} onSaved={load} />
    </Card>
  );
};
